import React from 'react';
import cn from 'classnames';
import { Trans } from '@lingui/macro';
import { Badge } from '@economic/taco';

import ExternalLink from '../../../../shared/components/ExternalLink';
import { ChangeSubscriptionPlan } from './types';
import PackageCardFunctionalities from './PackageCardComponents/PackageCardFunctionalities';
import PackageCardButtons from './PackageCardComponents/PackageCardButtons';
import PackageCardPrices from './PackageCardComponents/PackageCardPrices';

export default function PackageCard({
    planId,
    header,
    isDowngrade,
    contentText,
    readMoreLink,
    prices,
    isTrialUpgrade,
    size,
    className = '',
    highlighted = false,
    disabled = false,
    disabledReason = undefined,
    functionalityHeader,
    functionalities,
    redirectToSubscriptionStatusPage = true,
}: {
    planId: number;
    header: string;
    isDowngrade: boolean;
    contentText: string;
    readMoreLink: string | undefined;
    prices: ChangeSubscriptionPlan;
    isTrialUpgrade: boolean;
    size: 'sm' | 'md';
    className?: string | undefined;
    highlighted: boolean;
    disabled: boolean;
    disabledReason?: string | undefined;
    functionalityHeader: string;
    functionalities: { text: string; header?: boolean }[];
    redirectToSubscriptionStatusPage?: boolean;
}) {
    const sizeClasses = size === 'md' ? 'w-64 min-w-64' : 'w-56 min-w-56';
    return (
        <div className={cn('rounded-xl', className, sizeClasses)} style={{ boxShadow: '0px 0px 15px 1px rgba(0, 0, 0, 0.1)' }}>
            <div className="flex justify-between p-4">
                <h3>{header}</h3>
                {highlighted && (
                    <Badge color="blue" className="mt-1">
                        {!isTrialUpgrade ? <Trans>Current</Trans> : <Trans>Suggested</Trans>}
                    </Badge>
                )}
            </div>
            <div className="h-32 px-4">
                {contentText}
                {readMoreLink && (
                    <div className="mt-2">
                        <ExternalLink href={readMoreLink}>
                            <Trans>Read more</Trans>
                        </ExternalLink>
                    </div>
                )}
            </div>
            <PackageCardButtons
                planId={planId}
                isDowngrade={isDowngrade}
                disabled={disabled}
                disabledReason={disabledReason}
                readMoreLink={readMoreLink}
                highlighted={highlighted}
                isTrialUpgrade={isTrialUpgrade}
                header={header}
                redirectToSubscriptionStatusPage={redirectToSubscriptionStatusPage}
            />
            <PackageCardPrices prices={prices} />
            <PackageCardFunctionalities
                planId={planId}
                functionalityHeader={functionalityHeader}
                functionalities={functionalities}
            />
            <div className="flex justify-center pb-4">
                <ExternalLink href="https://www.e-conomic.dk/priser#funktionsliste">
                    <Trans>See full features list</Trans>
                </ExternalLink>
            </div>
        </div>
    );
}
